import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@lib/utils'

export interface ToggleProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Toggle label */
  label?: string
  /** Description below label */
  description?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Label position */
  labelPosition?: 'left' | 'right'
}

/**
 * Toggle/Switch component with brutalist styling
 *
 * @example
 * <Toggle label="Enable notifications" />
 * <Toggle label="Dark mode" checked />
 */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      className,
      label,
      description,
      size = 'md',
      labelPosition = 'right',
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const toggleId = id || `toggle-${Math.random().toString(36).slice(2, 9)}`

    const toggleElement = (
      <div className="relative flex items-center">
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          id={toggleId}
          disabled={disabled}
          className="peer sr-only"
          {...props}
        />
        {/* Track */}
        <div
          className={cn(
            // Base styles
            'relative bg-surface-overlay border-2 border-border',
            'transition-all cursor-pointer',
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',

            // Size
            size === 'sm' && 'h-5 w-9 rounded-sm',
            size === 'md' && 'h-6 w-11 rounded-sm',
            size === 'lg' && 'h-7 w-14 rounded-sm',

            // Checked state
            'peer-checked:bg-primary peer-checked:border-primary',

            // Disabled state
            'peer-disabled:cursor-not-allowed peer-disabled:opacity-50'
          )}
          onClick={() => {
            if (!disabled) {
              document.getElementById(toggleId)?.click()
            }
          }}
        >
          {/* Thumb */}
          <div
            className={cn(
              'absolute top-1/2 -translate-y-1/2 bg-text-primary',
              'transition-all duration-200 ease-spring',

              // Size and position
              size === 'sm' && 'h-3 w-3 left-0.5 peer-checked:left-[calc(100%-14px)]',
              size === 'md' && 'h-4 w-4 left-0.5 peer-checked:left-[calc(100%-18px)]',
              size === 'lg' && 'h-5 w-5 left-0.5 peer-checked:left-[calc(100%-22px)]',

              // Checked color
              'peer-checked:bg-text-inverse'
            )}
          />
        </div>
      </div>
    )

    const labelElement = (label || description) && (
      <div className="flex flex-col">
        {label && (
          <label
            htmlFor={toggleId}
            className={cn(
              'text-text-primary cursor-pointer select-none',
              size === 'sm' && 'text-body-sm',
              size === 'md' && 'text-body',
              size === 'lg' && 'text-body-lg',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {label}
          </label>
        )}
        {description && (
          <p className="text-caption text-text-muted mt-0.5">{description}</p>
        )}
      </div>
    )

    return (
      <div className={cn('flex items-start gap-3', className)}>
        {labelPosition === 'left' && labelElement}
        {toggleElement}
        {labelPosition === 'right' && labelElement}
      </div>
    )
  }
)

Toggle.displayName = 'Toggle'

export default Toggle
